import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';
import { useEscapeKey, useModalTransition, MS } from './use-modal';
import { ModalHeader, Backdrop } from './modal-parts';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: ModalSize;
  dismissible?: boolean;
};

const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-[28rem]',
  md: 'max-w-[32rem]',
  lg: 'max-w-[42rem]',
  xl: 'max-w-[48rem]',
};

const PANEL_BASE = cn(
  'relative z-10 flex w-full max-h-[85vh] flex-col overflow-hidden rounded-xl',
  'border border-white/[0.06] bg-surface/80 shadow-lg backdrop-blur-xl',
  'transition-all ease-out'
);

function panelCls(visible: boolean, size: ModalSize): string {
  return cn(
    PANEL_BASE,
    SIZES[size],
    visible
      ? 'opacity-100 scale-100 translate-y-0'
      : 'opacity-0 scale-[0.97] translate-y-2'
  );
}

type OverlayProps = ModalProps & { visible: boolean };

const NOOP = (): void => {};

function ModalOverlay(p: OverlayProps): ReactNode {
  const dur = { transitionDuration: `${MS}ms` };
  const dismissible = p.dismissible !== false;
  const close = dismissible ? p.onClose : NOOP;
  const headerProps = dismissible
    ? { title: p.title, onClose: p.onClose }
    : { title: p.title };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-base">
      <Backdrop visible={p.visible} dur={dur} onClose={close} />
      <div className={panelCls(p.visible, p.size ?? 'md')} style={dur}>
        <div className="shrink-0 px-md pt-md sm:px-lg sm:pt-lg">
          <ModalHeader {...headerProps} />
        </div>
        <div className="flex-1 overflow-y-auto px-md pb-md sm:px-lg sm:pb-lg">
          {p.children}
        </div>
      </div>
    </div>
  );
}

export function Modal(p: ModalProps): ReactNode {
  const dismissible = p.dismissible !== false;
  useEscapeKey(p.onClose, p.open && dismissible);
  const { mounted, visible } = useModalTransition(p.open);
  if (!mounted) return null;
  return createPortal(<ModalOverlay {...p} visible={visible} />, document.body);
}
